import {
    Box,
    Card,
    CardContent,
    IconButton,
    Snackbar,
    Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import CheckIcon from '@material-ui/icons/Check'
import clsx from 'clsx'
import React, { useMemo, useState, useCallback } from 'react'
import { useClashCardStyles } from '../styles'
import { BestPracticeClash } from '../types'
import { useResolveClash } from '../hooks'

interface ClashCardProps {
    clash: BestPracticeClash
    onResolved?: (clashId: string) => void
    onError?: (error: Error) => void
}

/**
 * Card component displaying a single Best Practice clash.
 * Shows clash details, allows resolution, and displays expandable information.
 *
 * Related PRs: 177160, 177715, 178091
 */
export const ClashCard = ({ clash, onResolved, onError }: ClashCardProps) => {
    const classes = useClashCardStyles({ isResolved: clash.isResolved })
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const handleError = useCallback((error: Error) => {
        setErrorMessage(error.message || 'Failed to resolve clash. Please try again.')
        onError?.(error)
    }, [onError])

    const handleCloseError = useCallback(() => {
        setErrorMessage(null)
    }, [])

    const { resolveClash, loading } = useResolveClash({
        onCompleted: (clashId) => {
            onResolved?.(clashId)
        },
        onError: handleError,
    })

    const acceptClasses = useMemo(
        () =>
            clash.isResolved
                ? classes.disabledActionIcon
                : clsx(classes.actionIconClashCard, 'acceptIconButton'),
        [clash.isResolved, classes.disabledActionIcon, classes.actionIconClashCard]
    )

    const handleResolve = () => {
        if (!clash.isResolved) {
            resolveClash(clash.id)
        }
    }

    const displayText = clash.contentTextPreview?.trim()?.length
        ? clash.contentTextPreview
        : clash.errorMessage

    return (
        <Card
            className={clsx(classes.card, {
                [classes.resolvedCard]: clash.isResolved,
            })}
            elevation={clash.isResolved ? 0 : 2}
        >
            <CardContent classes={{ root: classes.cardContent }}>
                <Box className={classes.header}>
                    <Box className={classes.content}>
                        {(clash.contentTextPreview || clash.errorMessage) && (
                            <Box className={classes.contentPreview}>
                                <Typography
                                    variant="caption"
                                    display="block"
                                    className={classes.contentText}
                                >
                                    {displayText}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {!clash.isResolved && (
                        <IconButton
                            type="button"
                            data-testid="icon-button-accept"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleResolve()
                            }}
                            className={acceptClasses}
                            aria-label="accept"
                            disabled={clash.isResolved || loading}
                            size="small"
                        >
                            <CheckIcon className={classes.resolveClashIcon} />
                        </IconButton>
                    )}
                </Box>
            </CardContent>

            {/* Error Snackbar */}
            <Snackbar
                open={!!errorMessage}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error" variant="filled">
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Card>
    )
}
